import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { apiClient } from '../../lib/api/client'
import type { CatalogSeries } from './types'

/**
 * The narrow seam this hook needs — `GET /catalog/categories/{category}/series`
 * (UID-286), mirroring `useCategoryTaxonomy.ts`'s `CategoryListingService`
 * pattern: an injectable interface so tests never hit the network.
 *
 * UID-387 — `specs` narrows the listing EXISTENTIALLY upstream (a series is
 * returned iff it contains at least one item matching the whole selection).
 * This retires `useFilteredSeriesCodes.ts`'s item-search-and-intersect
 * workaround entirely: the real `/series` route now narrows server-side, so
 * `CatalogSeriesPage` calls this directly instead of re-fetching `/search`
 * and deriving a matching-`seriesCode` set on the client.
 */
export interface CatalogSeriesService {
  listSeries(
    category: string,
    specs?: Readonly<Record<string, string>>,
    /**
     * Exact-match series-grain brand filter (UID-416 / ADR-0131 D2/D10) — a
     * THIRD, independent optional param, never folded into `specs` (a
     * different attribute at a different grain; see the port-level doc
     * comment on `apps/api`'s `CatalogSearchPort.listSeries`). Composes with
     * `specs` by AND. Blank/absent is treated as absent.
     */
    brand?: string,
  ): Promise<readonly CatalogSeries[]>
}

/** Pull the `title` off a Problem+JSON error body for a user-facing message
 * (same mapping `useCatalogSearch.ts`'s `problemTitle` uses). */
function problemTitle(error: unknown, fallback: string): string {
  return typeof error === 'object' && error !== null && 'title' in error
    ? String((error as { title?: unknown }).title)
    : fallback
}

async function bffListSeries(
  category: string,
  specs?: Readonly<Record<string, string>>,
  brand?: string,
): Promise<readonly CatalogSeries[]> {
  const { data, error } = await apiClient.GET('/catalog/categories/{category}/series', {
    params: {
      path: { category },
      query: {
        ...(specs !== undefined && Object.keys(specs).length > 0 ? { specs } : {}),
        // UID-416: omitted entirely when absent/blank — the unscoped/specs-
        // only request stays byte-identical (mirrors the BFF's own D6 guarantee).
        ...(brand !== undefined && brand.trim().length > 0 ? { brand } : {}),
      },
    },
  })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t load this category’s series.'))
  }
  return data.series
}

export const defaultCatalogSeriesService: CatalogSeriesService = {
  listSeries: bffListSeries,
}

export type CatalogSeriesState =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'ready'; readonly series: readonly CatalogSeries[] }

export type UseCatalogSeriesResult = CatalogSeriesState & {
  /** Re-runs `listSeries(category, selection)` — the "Try again" action for
   * the error state. */
  readonly retry: () => void
}

/**
 * Data-layer seam for the series-card grid screen (UID-286): fetches the
 * series cards for one leaf `category`, existentially narrowed by `selection`
 * (UID-387 — absent/empty is the unchanged unscoped listing; a non-empty
 * selection narrows server-side to series containing at least one matching
 * item). Re-fetches whenever `category` OR `selection`'s CONTENT changes (a
 * route-param change, or a facet checkbox toggle) and on `retry()`.
 *
 * `status: 'loading'` fires on EVERY fetch, including a selection-change
 * re-fetch, not just the initial mount — per the plan's disclosed perf note,
 * a narrowing `/series?specs=...` call can take several seconds for a broad
 * selection, so the grid must show a visible pending state for that window
 * rather than silently keeping the previous, now-stale cards on screen with
 * no affordance that a narrower answer is on the way.
 *
 * An empty `series` array is a valid `ready` state (the contract's "unknown
 * or blank category → `{ series: [] }`, never 404") — the caller renders an
 * empty state for that, not an error.
 */
export function useCatalogSeries(
  category: string,
  selection: Readonly<Record<string, string>> = {},
  service: CatalogSeriesService = defaultCatalogSeriesService,
  /** UID-416 / ADR-0131 D10 — the series-grain brand filter carried from the
   * URL (`CatalogSeriesPage` reads `?brand=`). Absent/blank re-fetches the
   * unscoped-or-specs-only listing, same as an empty `selection`. */
  brand?: string,
): UseCatalogSeriesResult {
  const selectionKey = JSON.stringify(selection)
  // A caller may rebuild `selection` with a new object identity every render
  // (as `CatalogSeriesPage` does, deriving it fresh from `URLSearchParams`).
  // Re-keying this memo on `selectionKey` (serialized CONTENT) rather than
  // `selection` itself means `stableSelection` keeps the SAME reference
  // across renders whose selection is unchanged in content, so `load` below
  // only gets recreated (and the effect only re-runs) on a real change —
  // same pattern `useFilteredSeriesCodes.ts` used to document, now retired.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on `selectionKey`, not the `selection` object identity — that's the whole point of this memo (see the comment above).
  const stableSelection = useMemo(() => selection, [selectionKey])
  const hasSelection = Object.keys(stableSelection).length > 0
  const [state, setState] = useState<CatalogSeriesState>({ status: 'loading' })
  const serviceRef = useRef(service)
  serviceRef.current = service

  const load = useCallback(() => {
    setState({ status: 'loading' })
    let cancelled = false
    serviceRef.current
      .listSeries(category, hasSelection ? stableSelection : undefined, brand)
      .then((series) => {
        if (cancelled) return
        setState({ status: 'ready', series })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const message =
          error instanceof Error ? error.message : 'We couldn’t load this category’s series.'
        setState({ status: 'error', message })
      })
    return () => {
      cancelled = true
    }
    // `service` is read only via the ref, so `category`/`stableSelection`/
    // `hasSelection`/`brand` are the only real dependencies.
  }, [category, stableSelection, hasSelection, brand])

  useEffect(() => load(), [load])

  return { ...state, retry: load }
}
